// Menu Structure Configuration for B2B Portal
// Separate menus for internal users vs B2B customers

import { routeAccessRules } from '@/router/guards'

// Helper function to check if user can access a specific route
function canAccessRoute(routePath, user, permissions) {
  const rules = routeAccessRules[routePath]
  if (!rules) {
    return true // No rules defined - allow access
  }
  
  // Check authentication requirement
  if (rules.requiresAuth && !user) {
    return false
  }
  
  // Check user type
  const userType = user?.clientType
  if (!rules.allowedUserTypes.includes(userType)) {
    return false
  }
  
  // Check permissions (for internal users only)
  if (rules.requiresPermission) {
    // B2B customers don't have granular permissions
    if (userType === 'B2B') {
      return false
    }
    
    // Internal users need specific permissions
    const isInternalUser = userType === 'internal' || userType === 'admin'
    if (isInternalUser) {
      // Admin/super_admin/owner bypass
      if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'owner') {
        return true
      }
      // Check actual permission or owner role
      return permissions?.[rules.requiresPermission] === true || user?.role === 'owner'
    }
  }
  
  return true
}

export const INTERNAL_MENU = [
  {
    title: 'Command Center',
    icon: 'mdi-view-dashboard',
    to: '/',
    permission: 'b2b_dashboard.access',
    showForInternalUsers: true
  },
  
  // Client Management Section
  {
    divider: true,
    header: 'CLIENT OPERATIONS'
  },
  {
    title: 'Client Directory',
    icon: 'mdi-domain',
    to: '/clients',
    permission: 'b2b_clients.view',
    showForInternalUsers: true,
    badge: { color: 'primary', content: 'new' }
  },
  {
    title: 'Place Order for Client',
    icon: 'mdi-cart-plus',
    to: '/sales-order-entry',
    permission: 'b2b_orders.create_for_client',
    showForInternalUsers: true
  },
  {
    title: 'Order Pipeline',
    icon: 'mdi-clipboard-list',
    to: '/order-management',
    permission: 'b2b_orders.view_all',
    showForInternalUsers: true,
    badge: { color: 'error', content: '!' }
  },
  
  // Financial Section
  {
    divider: true,
    header: 'FINANCIAL'
  },
  {
    title: 'Credit Management',
    icon: 'mdi-bank',
    to: '/credit-management',
    permission: 'b2b_credit.view',
    showForInternalUsers: true
  },
  {
    title: 'Invoices & Payments',
    icon: 'mdi-receipt',
    to: '/invoices',
    permission: 'b2b_orders.view_all',
    showForInternalUsers: true
  },
  
  // Sales Tools
  {
    divider: true,
    header: 'SALES TOOLS'
  },
  {
    title: 'Product Catalog',
    icon: 'mdi-bottle-wine',
    to: '/products',
    showForAll: true
  },
  {
    title: 'Price Lists',
    icon: 'mdi-tag-multiple',
    to: '/pricing',
    permission: 'b2b_orders.view_all'
  },
  {
    title: 'Sales Reports',
    icon: 'mdi-chart-line',
    to: '/reports',
    permission: 'b2b_orders.view_all',
    showForInternalUsers: true
  },
  
  // Team Management (for managers only)
  {
    divider: true,
    header: 'ADMINISTRATION',
    requiresRole: ['admin', 'manager']
  },
  {
    title: 'Team Management',
    icon: 'mdi-account-group',
    to: '/team',
    permission: 'b2b_teams.manage',
    requiresRole: ['admin', 'manager']
  },
  {
    title: 'User Management',
    icon: 'mdi-account-multiple',
    to: '/users',
    permission: 'b2b_teams.manage',
    showForInternalUsers: true
  },
  {
    title: 'System Settings',
    icon: 'mdi-cog',
    to: '/settings',
    requiresRole: ['admin']
  }
];

export const B2B_CUSTOMER_MENU = [
  {
    title: 'Dashboard',
    icon: 'mdi-view-dashboard',
    to: '/'
  },
  
  // Ordering Section
  {
    divider: true,
    header: 'ORDERING'
  },
  {
    title: 'Product Catalog',
    icon: 'mdi-bottle-wine',
    to: '/products'
  },
  {
    title: 'Quick Order',
    icon: 'mdi-cart-arrow-down',
    to: '/quick-order'
  },
  {
    title: 'Order Templates',
    icon: 'mdi-content-copy',
    to: '/templates',
    badge: { color: 'info', content: 'save time' }
  },
  
  // Account Management
  {
    divider: true,
    header: 'MY ACCOUNT'
  },
  {
    title: 'Order History',
    icon: 'mdi-receipt',
    to: '/orders'
  },
  {
    title: 'Invoices',
    icon: 'mdi-file-document',
    to: '/invoices'
  },
  {
    title: 'Credit Status',
    icon: 'mdi-credit-card-check',
    to: '/credit'
  },
  
  // Company Management (for owners/managers)
  {
    divider: true,
    header: 'COMPANY',
    requiresRole: ['owner', 'manager']
  },
  {
    title: 'Team Members',
    icon: 'mdi-account-multiple',
    to: '/company/team',
    requiresRole: ['owner', 'manager']
  },
  {
    title: 'Delivery Addresses',
    icon: 'mdi-map-marker-multiple',
    to: '/company/addresses',
    requiresRole: ['owner', 'manager']
  },
  {
    title: 'Company Profile',
    icon: 'mdi-domain',
    to: '/company/profile',
    requiresRole: ['owner', 'manager']
  },
  
  // Support
  {
    divider: true,
    header: 'SUPPORT'
  },
  {
    title: 'Contact Sales Rep',
    icon: 'mdi-phone',
    to: '/contact'
  },
  {
    title: 'Help Center',
    icon: 'mdi-help-circle',
    to: '/help'
  }
];

// Helper function to filter menu items based on user permissions/role
export function filterMenuItems(menuItems, user, permissions) {
  
  // Check if user is internal based on role
  const internalRoles = ['admin', 'super_admin', 'manager', 'employee', 'employee_rep', 'sales_rep', 'owner'];
  const isInternalUser = user?.clientType === 'internal' || 
                        user?.clientType === 'admin' || 
                        internalRoles.includes(user?.role);
  
  return menuItems.filter(item => {
    // Always show dividers and headers
    if (item.divider || item.header) {
      // Check if header has role requirements
      if (item.requiresRole) {
        const roles = Array.isArray(item.requiresRole) ? item.requiresRole : [item.requiresRole];
        return roles.includes(user?.role);
      }
      return true;
    }
    
    // Check if user can access the route
    if (item.to && !canAccessRoute(item.to, user, permissions)) {
      return false;
    }
    
    // Check role requirements
    if (item.requiresRole) {
      const roles = Array.isArray(item.requiresRole) ? item.requiresRole : [item.requiresRole];
      if (!roles.includes(user?.role)) {
        return false;
      }
    }
    
    // Check permission requirements
    if (item.permission && !item.showForAll) {
      // For internal users, show items marked for internal users or check permissions
      if (isInternalUser) {
        // Always show items marked for internal users
        if (item.showForInternalUsers) {
          return true;
        }
        // Otherwise check actual permissions - be permissive for internal users
        return permissions?.[item.permission] === true || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'owner';
      }
      // B2B customers don't have granular permissions
      return false;
    }
    
    return true;
  });
}

// Get the appropriate menu based on user type
export function getMenuForUser(user) {
  // Internal users and DTC employees see the internal menu
  if (user?.clientType === 'internal' || user?.clientType === 'admin' || user?.clientType === 'dtc') {
    return INTERNAL_MENU;
  }
  
  // Also check for internal roles even if clientType isn't set properly
  const internalRoles = ['admin', 'super_admin', 'manager', 'employee', 'employee_rep', 'sales_rep', 'owner'];
  if (internalRoles.includes(user?.role)) {
    return INTERNAL_MENU;
  }
  
  // B2B customers see their own menu
  return B2B_CUSTOMER_MENU;
}